import { Box, HStack, VStack } from "@chakra-ui/react";
import { useMemo, type Ref } from "react";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { ScheduledBudgetAdFlightUtcBounds } from "../../utils/eventWallTime";
import type { BudgetData, BudgetPhaseForm } from "./BudgetSelector";
import { GuidedBudgetAmountSummary } from "./GuidedBudgetAmountSummary";
import { GuidedBudgetEditorHeader } from "./GuidedBudgetEditorHeader";
import { CampaignRunScheduleBlock } from "./CampaignRunScheduleBlock";
import { GuidedBudgetScheduledPhasesSection } from "./GuidedBudgetScheduledPhasesSection";
import { GuidedBudgetTypeSelect } from "./GuidedBudgetTypeSelect";
import { sumPhaseAmounts } from "./guidedBudgetEditorMoney";
import { NO_SCHEDULED_SEGMENTS } from "./guidedBudgetEditorTokens";
import { useGuidedBudgetScheduledMeta } from "../../hooks/useGuidedBudgetScheduledMeta";

export type GuidedBudgetMode = "daily" | "lifetime" | "scheduled";

export type GuidedBudgetEditorProps = {
  budget?: BudgetData;
  /** IANA zone for interpreting phase datetime-local values as wall time (same as campaign schedule). */
  eventTimezone?: string;
  /**
   * When set (from ad set run schedules or create-flow schedule), phased budget times are
   * constrained to this window in the UI and on patch (same checks as save / publish).
   */
  scheduledBudgetFlightBounds?: ScheduledBudgetAdFlightUtcBounds | null;
  /** When false, only daily/lifetime (e.g. quick edit modal). */
  allowScheduled?: boolean;
  /**
   * When true, the daily / lifetime / scheduled selector is read-only (e.g. campaign already linked
   * to Meta via `external_id`).
   */
  disableBudgetTypeChange?: boolean;
  onChangeBudgetType: (type: GuidedBudgetMode) => void;
  onChangeBudgetAmount: (amount: number | null) => void;
  onAddSegment?: () => void;
  onRemoveSegment?: (index: number) => void;
  onChangeSegment?: (index: number, patch: Partial<BudgetPhaseForm>) => void;
  /** When set (e.g. modal initial focus), forwards to the dollar amount input for simple budgets. */
  amountInputRef?: Ref<HTMLInputElement>;
  /**
   * When defined (including `""`), timeline / overlap / flight messages come from the parent
   * (e.g. Yup + RHF) instead of duplicating checks here. Omit to use local validation (create flow).
   */
  externalScheduledPhaseTimelineMessage?: string | undefined;
  /**
   * When set (e.g. Edit budget modal + RHF), show validation text per phase row and suppress the
   * duplicate global timeline alert above “Add Budget Phase”.
   */
  scheduledBudgetSegmentRowErrorMessages?: Map<number, string>;
  /**
   * First invalid phased field for a red outline (times or amount), aligned with Yup paths.
   */
  scheduledBudgetFieldOutline?:
    | { kind: "time_start" | "time_end"; segmentIndex: number }
    | { kind: "amount"; segmentIndex: number }
    | null;
  /**
   * When set, campaign run dates render in their own card **above** the budget card (always).
   * Omit when there is no run schedule to edit in this surface (e.g. budget-only modal).
   */
  campaignRunSchedule?: {
    startAt: string;
    endAt: string;
    onChangeStartAt: (value: string) => void;
    onChangeEndAt: (value: string) => void;
    /** When true, run dates are display-only (e.g. linked campaign with phased scheduled budget). */
    readOnly?: boolean;
    /** Shown below the schedule when {@link campaignRunSchedule.readOnly} is true. */
    readOnlyHelperText?: string;
  } | null;
};

dayjs.extend(utc);
dayjs.extend(timezone);

export default function GuidedBudgetEditor({
  budget,
  eventTimezone = GUIDED_EDIT_DEFAULT_TIMEZONE,
  scheduledBudgetFlightBounds = null,
  allowScheduled = true,
  disableBudgetTypeChange = false,
  onChangeBudgetType,
  onChangeBudgetAmount,
  onAddSegment,
  onRemoveSegment,
  onChangeSegment,
  amountInputRef,
  externalScheduledPhaseTimelineMessage,
  scheduledBudgetSegmentRowErrorMessages,
  scheduledBudgetFieldOutline,
  campaignRunSchedule = null,
}: GuidedBudgetEditorProps) {
  const mode: GuidedBudgetMode =
    budget?.type === "scheduled"
      ? "scheduled"
      : budget?.type === "daily"
        ? "daily"
        : "lifetime";

  const isScheduled = mode === "scheduled";

  const { flightRangeInEventZone, scheduledPhaseTimelineError, phaseNeighborPickerBounds } =
    useGuidedBudgetScheduledMeta({
      isScheduled,
      budget,
      eventTimezone,
      scheduledBudgetFlightBounds,
      externalScheduledPhaseTimelineMessage,
    });

  const scheduledSegments = budget?.segments ?? NO_SCHEDULED_SEGMENTS;

  const scheduledTotal = useMemo(
    () => sumPhaseAmounts(scheduledSegments),
    [scheduledSegments]
  );

  const cardContainerProps = {
    p: 6,
    borderRadius: "2xl",
    border: "1px solid",
    borderColor: "#DDDDDD",
    bg: "white",
    w: "full",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
  } as const;

  return (
    <VStack align="stretch" spacing={4} w="full">
      {campaignRunSchedule ? (
        <Box {...cardContainerProps}>
          <CampaignRunScheduleBlock
            eventTimezone={eventTimezone}
            startAt={campaignRunSchedule.startAt}
            endAt={campaignRunSchedule.endAt}
            onChangeStartAt={campaignRunSchedule.onChangeStartAt}
            onChangeEndAt={campaignRunSchedule.onChangeEndAt}
            isScheduleRangeReadOnly={campaignRunSchedule.readOnly === true}
            scheduleReadOnlyHelperText={campaignRunSchedule.readOnlyHelperText}
            pickerOpenIconAtStart
          />
        </Box>
      ) : null}

      <Box {...cardContainerProps}>
        <VStack align="stretch" spacing={3} w="full">
          <GuidedBudgetEditorHeader />

          <HStack
            align="stretch"
            spacing={{ base: 3, md: 6 }}
            w="full"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <GuidedBudgetTypeSelect
              mode={mode}
              allowScheduled={allowScheduled}
              disableBudgetTypeChange={disableBudgetTypeChange}
              onChangeBudgetType={onChangeBudgetType}
            />
            <GuidedBudgetAmountSummary
              isScheduled={isScheduled}
              scheduledTotal={scheduledTotal}
              budget={budget}
              amountInputRef={amountInputRef}
              onChangeBudgetAmount={onChangeBudgetAmount}
            />
          </HStack>

          {isScheduled ? (
            <GuidedBudgetScheduledPhasesSection
              scheduledSegments={scheduledSegments}
              eventTimezone={eventTimezone}
              flightRangeInEventZone={flightRangeInEventZone}
              scheduledBudgetFlightBounds={scheduledBudgetFlightBounds}
              phaseNeighborPickerBounds={phaseNeighborPickerBounds}
              scheduledPhaseTimelineError={scheduledPhaseTimelineError}
              scheduledBudgetSegmentRowErrorMessages={
                scheduledBudgetSegmentRowErrorMessages
              }
              scheduledBudgetFieldOutline={scheduledBudgetFieldOutline}
              budget={budget}
              onAddSegment={onAddSegment}
              onRemoveSegment={onRemoveSegment}
              onChangeSegment={onChangeSegment}
            />
          ) : null}
        </VStack>
      </Box>
    </VStack>
  );
}
